import { useEffect, useRef, useState } from 'react';
import {
  Box,
  HStack,
  Progress,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import type { RiskDetailsSync } from 'api/quote-marketplace';

const HOLD_AT_OPTIONS = [88, 91, 93, 97];
const HOLD_SECONDS = 3;
const CREEP_TO = 99;
const SECONDS_PER_CREEP_PERCENT = 2;
const TICK_MS = 250;
const HIDE_AFTER_MS = 2000;

interface RiskDetailsSyncProgressProps {
  sync: RiskDetailsSync | undefined;
}

const RiskDetailsSyncProgress = ({ sync }: RiskDetailsSyncProgressProps) => {
  const trackBg = useColorModeValue('gray.200', 'whiteAlpha.200');
  const isPending = sync?.status === 'pending';
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(isPending);
  const anchorRef = useRef({ seconds: 0, at: Date.now() });

  useEffect(() => {
    if (isPending) {
      anchorRef.current = {
        seconds: sync?.pending_seconds ?? 0,
        at: Date.now(),
      };
    }
  }, [isPending, sync?.pending_seconds]);

  useEffect(() => {
    if (!isPending) return undefined;
    const elapsed = () =>
      anchorRef.current.seconds + (Date.now() - anchorRef.current.at) / 1000;

    const holdAt =
      HOLD_AT_OPTIONS[Math.floor(Math.random() * HOLD_AT_OPTIONS.length)];

    setIsVisible(true);
    setProgress(progressAfter(elapsed(), holdAt));

    const interval = setInterval(
      () =>
        setProgress((current) =>
          Math.max(current, progressAfter(elapsed(), holdAt)),
        ),
      TICK_MS,
    );
    return () => clearInterval(interval);
  }, [isPending]);

  useEffect(() => {
    if (isPending || !isVisible) return undefined;
    setProgress(100);
    const timeout = setTimeout(() => {
      setIsVisible(false);
      setProgress(0);
    }, HIDE_AFTER_MS);
    return () => clearTimeout(timeout);
  }, [isPending, isVisible]);

  const progressAfter = (seconds: number, holdAt: number) => {
    if (seconds <= 10) return seconds * 5;
    if (seconds <= 20) return 50 + ((seconds - 10) * (holdAt - 50)) / 10;
    const creeping = seconds - 20 - HOLD_SECONDS;
    if (creeping <= 0) return holdAt;
    return Math.min(
      CREEP_TO,
      holdAt + Math.floor(creeping / SECONDS_PER_CREEP_PERCENT),
    );
  };

  if (!isVisible) return null;

  const isError = !isPending && sync?.status === 'error';
  const label = isPending
    ? 'Capturing risk details in Acturis…'
    : isError
      ? 'Acturis did not accept the risk details'
      : 'Risk details captured';

  return (
    <Box role="status" aria-live="polite" w="100%">
      <HStack justify="space-between" spacing={2} mb={1}>
        <Text fontSize="xs" fontWeight="normal" textTransform="none">
          {label}
        </Text>
        <Text
          fontSize="xs"
          fontWeight="normal"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {Math.round(progress)}%
        </Text>
      </HStack>
      <Progress
        w="100%"
        bg={trackBg}
        value={progress}
        size="xs"
        colorScheme={isError ? 'red' : 'yellow'}
        borderRadius="md"
        hasStripe={isPending}
        isAnimated={isPending}
        sx={{ '& > div': { transition: 'width 0.5s ease-out' } }}
      />
    </Box>
  );
};

export default RiskDetailsSyncProgress;
